import {
  AccountInfoResponse,
  CONVERSION_COHORTS,
  CONVERSION_STATES,
} from 'types/billing';
import { QaBillingState } from './quotaFixtures';

// Account-info fixtures, keyed by the SAME qa_billing_state values as
// QUOTA_FIXTURES so a single ?qa_billing_state=<state> drives both the /quota
// and /account-info mock sources together (D26 coupled freshness).
//
// Trial lives here (not in QUOTA_FIXTURES) per the PLT-4042 split. camelCase to
// match the BE CamelModel wire format (Q5).
export const ACCOUNT_INFO_FIXTURES: Record<
  QaBillingState,
  AccountInfoResponse
> = {
  'trial-first-day': {
    selfServe: true,
    tier: 'free',
    stripeCustomerId: 'cus_trial_first_day',
    canManageBilling: true,
    trial: { active: true, daysRemaining: 14, dayNumber: 1, totalDays: 15 },
  },

  'trial-early': {
    selfServe: true,
    tier: 'free',
    stripeCustomerId: 'cus_trial_early',
    canManageBilling: true,
    trial: { active: true, daysRemaining: 12, dayNumber: 3, totalDays: 15 },
  },

  'trial-ending': {
    selfServe: true,
    tier: 'free',
    stripeCustomerId: 'cus_trial_ending',
    canManageBilling: true,
    trial: { active: true, daysRemaining: 2, dayNumber: 13, totalDays: 15 },
  },

  // Final day — folds into the `ending` band and simply reads "1 day left"
  // (days-only; no HH:MM:SS timer or "ends today"). Anchors the singular plural.
  'trial-last-day': {
    selfServe: true,
    tier: 'free',
    stripeCustomerId: 'cus_trial_last_day',
    canManageBilling: true,
    trial: { active: true, daysRemaining: 1, dayNumber: 14, totalDays: 15 },
  },

  healthy: {
    selfServe: true,
    tier: 'paid',
    stripeCustomerId: 'cus_healthy',
    canManageBilling: true,
    trial: null,
  },

  approaching: {
    selfServe: true,
    tier: 'paid',
    stripeCustomerId: 'cus_approaching',
    canManageBilling: true,
    trial: null,
  },

  overage: {
    selfServe: true,
    tier: 'paid',
    stripeCustomerId: 'cus_overage',
    canManageBilling: true,
    trial: null,
  },

  blocked: {
    selfServe: true,
    tier: 'paid',
    stripeCustomerId: 'cus_blocked',
    canManageBilling: true,
    trial: null,
  },

  'blocked-trial-end': {
    selfServe: true,
    tier: 'free',
    stripeCustomerId: 'cus_blocked_trial_end',
    canManageBilling: true,
    trial: null,
  },

  // Trial object still present from /account-info but `active:false` — the date
  // has passed. Drives the PLT-4196 sidebar "Trial ended" fallback.
  'trial-lapsed': {
    selfServe: true,
    tier: 'free',
    stripeCustomerId: 'cus_trial_lapsed',
    canManageBilling: true,
    trial: { active: false, daysRemaining: 0, dayNumber: 15, totalDays: 15 },
  },

  'blocked-payment-failed': {
    selfServe: true,
    tier: 'paid',
    stripeCustomerId: 'cus_blocked_payment_failed',
    canManageBilling: true,
    trial: null,
  },

  'blocked-subscription-canceled': {
    selfServe: true,
    tier: 'paid',
    stripeCustomerId: 'cus_blocked_subscription_canceled',
    canManageBilling: true,
    trial: null,
  },

  // Migrated free user — trial active, welcome popup not yet seen
  'trial-migrated-free': {
    selfServe: true,
    tier: 'free',
    stripeCustomerId: 'cus_trial_migrated_free',
    canManageBilling: true,
    trial: { active: true, daysRemaining: 14, dayNumber: 1, totalDays: 14 },
    conversionState: CONVERSION_STATES.trialActive,
    conversionCohort: CONVERSION_COHORTS.free,
    migrationPerk: {
      active: true,
      percentOff: 50,
      durationMonths: 2,
    },
  },

  // Migrated free user who had an annual prepaid plan — shows refund note + Switch now button
  'trial-migrated-free-annual': {
    selfServe: true,
    tier: 'free',
    stripeCustomerId: 'cus_trial_migrated_free_annual',
    canManageBilling: true,
    trial: { active: true, daysRemaining: 14, dayNumber: 1, totalDays: 14 },
    conversionState: CONVERSION_STATES.trialActive,
    conversionCohort: CONVERSION_COHORTS.free,
    migrationPerk: {
      active: true,
      percentOff: 50,
      durationMonths: 2,
    },
  },

  // Migrated free user whose trial end date has lapsed (trial.active=false) but
  // the account is not yet hard-blocked. Drives TrialBannerEnded
  // on the billing page (home.trial-banner.ended-* copy surface).
  'trial-migrated-free-lapsed': {
    selfServe: true,
    tier: 'free',
    stripeCustomerId: 'cus_trial_migrated_free_lapsed',
    canManageBilling: true,
    trial: { active: false, daysRemaining: 0, dayNumber: 15, totalDays: 14 },
    conversionState: CONVERSION_STATES.trialActive,
    conversionCohort: CONVERSION_COHORTS.free,
    migrationPerk: {
      active: true,
      percentOff: 50,
      durationMonths: 2,
    },
  },
} as const;
